import * as React from 'react';
import { CheckIcon, ChevronDownIcon, Loader2, XIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

export interface ComboboxOption {
  value: string;
  label: React.ReactNode;
  /** Plain text matched against the query. Defaults to `label` when it is a string. */
  keywords?: string;
  /** Muted second line — a club's league, a country's region. */
  description?: React.ReactNode;
  /** Rendered before the label: a flag, a crest, an avatar. */
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface ComboboxProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: ComboboxOption[];
  placeholder?: React.ReactNode;
  searchPlaceholder?: string;
  emptyText?: React.ReactNode;
  /**
   * Server-side search. When set, the list is not filtered locally — the caller
   * refetches `options` from the query it is handed and the component shows
   * them as they are.
   */
  onSearch?: (query: string) => void;
  loading?: boolean;
  /** Shows an × that resets the value to `undefined`. */
  clearable?: boolean;
  disabled?: boolean;
  /**
   * What the trigger shows for the current value when it is not among
   * `options` — e.g. a selected club that the current search no longer returns.
   */
  selectedLabel?: React.ReactNode;
  size?: 'sm' | 'default';
  className?: string;
  contentClassName?: string;
  id?: string;
  'aria-invalid'?: boolean;
  /** Rendered after the options — "Add a club that isn't listed", say. */
  footer?: React.ReactNode;
  /**
   * A muted line above the options, replacing the empty state — "Type two
   * letters to search", "No clubs match". Shown even when pinned options (an
   * "All clubs" entry) keep the list from being empty.
   */
  hint?: React.ReactNode;
}

/**
 * A searchable single select (Popover + cmdk). Replaces antd's
 * `<Select showSearch>` and its remote-search variant.
 */
export const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>(function Combobox(
  {
    value,
    onChange,
    options,
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    emptyText = 'No results',
    onSearch,
    loading = false,
    clearable = false,
    disabled = false,
    selectedLabel,
    size = 'default',
    className,
    contentClassName,
    id,
    footer,
    hint,
    ...aria
  },
  ref,
) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const selected = options.find((option) => option.value === value);
  const display = selected?.label ?? (value ? selectedLabel : undefined);

  const onQueryChange = (next: string) => {
    setQuery(next);
    onSearch?.(next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next && query) onQueryChange('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          ref={ref}
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-invalid={aria['aria-invalid']}
          disabled={disabled}
          data-placeholder={display === undefined ? '' : undefined}
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-start text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none',
            'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
            'disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground',
            'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
            size === 'sm' ? 'h-8 text-[13px]' : 'h-9',
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2 truncate">
            {selected?.icon}
            <span className="truncate">{display ?? placeholder}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
            {clearable && value && !disabled ? (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear"
                className="rounded-sm p-0.5 hover:bg-accent hover:text-foreground"
                onPointerDown={(event) => {
                  // Clear without letting the trigger open the popover.
                  event.preventDefault();
                  event.stopPropagation();
                  onChange(undefined);
                }}
              >
                <XIcon className="size-3.5" />
              </span>
            ) : null}
            <ChevronDownIcon className="size-4 opacity-60" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn('w-(--radix-popover-trigger-width) min-w-56 p-0', contentClassName)}
      >
        <Command shouldFilter={!onSearch}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={onQueryChange}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-3 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
              </div>
            ) : hint ? (
              <div className="px-3 py-2.5 text-xs text-muted-foreground">{hint}</div>
            ) : (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            {options.length > 0 ? (
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[
                      option.keywords ?? (typeof option.label === 'string' ? option.label : ''),
                    ]}
                    disabled={option.disabled}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                      if (query) onQueryChange('');
                    }}
                  >
                    {option.icon}
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate">{option.label}</span>
                      {option.description ? (
                        <span className="truncate text-xs text-muted-foreground">
                          {option.description}
                        </span>
                      ) : null}
                    </div>
                    <CheckIcon
                      className={cn(
                        'ms-auto size-4',
                        value === option.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
          {footer ? <div className="border-t p-1">{footer}</div> : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
});
